/**
 * `useMembershipStatus` — reads the trusted membership classification from
 * the active session (028-membership-state-gating).
 *
 * Contract: `specs/028-membership-state-gating/contracts/membership-status.md`.
 *
 * Reads `AuthState.member.membershipStatus` (the server-derived classification,
 * D-113), NOT a client-only flag (FR-001 / SC-001). Post-032-cutover the live
 * member session is the single BFF `RealSessionProvider`, whose `session.me`
 * member arm ALWAYS carries the server-snapshotted `membershipStatus` (RD-105 /
 * RD-111) — so the value is present for every real member. Fail-closed: a member
 * session missing the field, or any non-member session, resolves to the most
 * restricted safe state (`FAIL_CLOSED_STATUS = 'none'`), so the gating never
 * grants a capability on assumed validity (FR-006 / SC-004).
 *
 * The demo-identity-port fallback (a member state with only a `demoMemberKey`,
 * classified through the since-removed demo classifier) is RETIRED at this cutover (C4):
 * the real adapter always sets `membershipStatus`. A member session that
 * reaches here without the field now fails closed to `none` rather than being
 * reclassified from a client-side discriminator.
 *
 * Admin distinctness (contract C7): an admin session carries no membership
 * context; admin surfaces use their own chrome and do not mount the member
 * capability layer, so this hook is consumed only in member contexts. The
 * fail-closed return for a non-member session is a safety default, not a
 * member classification of an admin.
 */

import { useSession } from '../session/useSession.js';
import { FAIL_CLOSED_STATUS, type MembershipStatus } from './membershipStatus.js';

export function useMembershipStatus(): MembershipStatus {
  const { state } = useSession();
  if (state.kind !== 'member') {
    return FAIL_CLOSED_STATUS;
  }
  // The trusted, server-derived classification carried on the member session.
  // Always present for a real member (the BFF `session.me` member arm sets it);
  // absent only on a degenerate fixture, which fails closed (FR-006).
  return state.membershipStatus ?? FAIL_CLOSED_STATUS;
}
